/* eslint-disable jsx-a11y/anchor-is-valid */

import * as Yup from 'yup'

import { Link, useLocation } from 'react-router-dom'

import clsx from 'clsx'
import { useAuth } from 'app/context/auth-context'
import { useFormik } from 'formik'
import { useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),

})

const initialValues = {
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {

  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cognitoUser, setCognitoUser] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const authContext = useAuth();
  const search = useLocation().search;
  const email = new URLSearchParams(search).get("email") || '';
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const formik = useFormik({
    initialValues: { ...initialValues, email },
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)

      authContext.login(values.email, values.password)
        .then(async () => {
          setLoading(false);
          try {
            const response = await authContext.login(values.email, values.password);
            if (response?.challengeName === 'NEW_PASSWORD_REQUIRED') {
              setCognitoUser(response); // Store the cognito user object
              setTempPassword(values.password);
              setShowChangePassword(true);
              setLoading(false);
            }
          } catch (error) {
            setLoading(false)
            setSubmitting(false)
            setStatus('The login detail is incorrect')
          }
        })
        .catch(() => {
          setLoading(false)
          setSubmitting(false)
          setStatus('The login detail is incorrect')
        });
    },
  });

  // Add the change password handler
  const handleChangePassword = async () => {
    setLoading(true);
    try {
      await authContext.completeNewPassword(cognitoUser, newPassword);
      await authContext.login(formik.values.email, newPassword);
      setShowChangePassword(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // Handle error appropriately
    }
  };

  return (
    <>
      <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
        <form
          className='form w-100'
          onSubmit={formik.handleSubmit}
          noValidate
          id='kt_login_signin_form'
        >
          {/* begin::Heading */}
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>Sign In</h1>
            <div className='text-gray-400 fw-bold fs-4'>
              New Here?{' '}
              <Link to='/auth/registration'>
                <span className='fw-bold text-primary'>Create an Employer account</span>
              </Link>
            </div>
          </div>
          {/* begin::Heading */}

          {formik.status && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}

          {/* begin::Form group */}
          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
            <input
              placeholder='Email'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                { 'is-invalid': formik.touched.email && formik.errors.email },
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                }
              )}
              type='email'
              name='email'
              autoComplete='off'
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            )}
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className='fv-row mb-10'>
            <div className='d-flex justify-content-between mt-n5'>
              <div className='d-flex flex-stack mb-2'>
                {/* begin::Label */}
                <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
                {/* end::Label */}

              </div>
            </div>
            <div className="input-group">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder='Password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              <button
                title={showPassword? 'hide password': 'show password'}
                type='button'
                className='btn btn-outline-secondary'
                onClick={togglePasswordVisibility}
              >
                <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}

            <div className='d-flex justify-content-end'>

              {/* begin::Link */}
              <Link
                to={'/auth/forgot-password?email=' + encodeURIComponent(formik.values.email)}
                className='link-primary fs-6 mt-2'
              >
                Forgot Password ?
              </Link>
            </div>
            {/* end::Link */}
          </div>
          {/* end::Form group */}

          {/* begin::Action */}
          <div className='text-center'>
            <button
              type='submit'
              id='kt_sign_in_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>Continue</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Action */}
        </form>
      </div>
      <Modal show={showChangePassword} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Change Password Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-dark'>New Password</label>
            <input
              type='password'
              className='form-control form-control-lg form-control-solid'
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-dark'>Confirm Password</label>
            <input
              type='password'
              className='form-control form-control-lg form-control-solid'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className='btn btn-lg btn-primary w-100 mb-5'
            onClick={handleChangePassword}
            disabled={loading || newPassword !== confirmPassword}
          >
            {!loading && <span className='indicator-label'>Change Password</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

